import React from 'react'
import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'

import styled, { useTheme } from 'styled-components/native'

import type { CareHistoryProvider } from '@lyrahealth-inc/shared-app-logic/src/models/providers/CareHistoryProviders'
import { BodyText, Size as BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { Avatar } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/Avatar'
import { MessageIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/MessageIcon'
import { PressableOpacity } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/pressableOpacity/PressableOpacity'
import { Modal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/modal/Modal'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { formatPastCareDates, getPastCareProgramLabel, type PastCareGroup } from './pastCare'
import { getAuthUserId } from '../../../../data/auth/authSelectors'
import { useProviderPairMessagingAccess } from '../../../messages/data/hooks/useProviderPairMessagingAccess'

// Reserve the shared absolute header's 64px plus a 16px gap before the body.
const MODAL_CONTENT_TOP_PADDING = 80

const ModalTitle = styled.View(({ theme }) => ({ flex: 1, paddingLeft: theme.spacing['48px'] }))
const Contents = styled.View(({ theme }) => ({ gap: theme.spacing['24px'] }))
const Group = styled.View(({ theme }) => ({ gap: theme.spacing['12px'] }))
const Row = styled.View(({ theme }) => ({ flexDirection: 'row', alignItems: 'center', gap: theme.spacing['12px'] }))
const ProviderInfo = styled.View({ flex: 1, minWidth: 0 })
const MessageButton = styled(PressableOpacity)<{ disabled: boolean }>(({ theme, disabled }) => ({
  padding: theme.spacing['8px'],
  opacity: disabled ? 0.4 : 1,
}))

export const PastCareModal = ({
  groups,
  onClose,
  onMessageProvider,
}: {
  groups: PastCareGroup[]
  onClose: () => void
  // Opens the top-right Message icon's popover to this provider's conversation.
  onMessageProvider: (providerId: string) => void
}) => {
  const intl = useIntl()
  const { colors } = useTheme()
  const providerId = useSelector(getAuthUserId)
  const { canAccessProviderPairs } = useProviderPairMessagingAccess()

  const canMessage = (provider: CareHistoryProvider) =>
    Boolean(canAccessProviderPairs && providerId && provider.can_message && provider.participant_lyra_id !== providerId)

  const title = intl.formatMessage({ defaultMessage: 'Past care', description: 'Past care history modal title' })
  return (
    <Modal
      visible
      accessibilityLabel={title}
      modalTitle={
        <ModalTitle>
          <BodyText testID={tID('PastCare-title')} text={title} />
        </ModalTitle>
      }
      onRequestClose={onClose}
      closeOnScrim
      scrollable
      scrollableModalWidth='min(560px, 95vw)'
      scrollableModalHeight='auto'
      scrollableModalMaxHeight='95vh'
      scrollableStyle={{ paddingTop: MODAL_CONTENT_TOP_PADDING }}
      modalContents={
        <Contents testID={tID('PastCare-modal')}>
          <BodyText
            regular
            size={BodyTextSize.SMALL}
            text={intl.formatMessage({
              defaultMessage: 'Limited to providers from the last 3 years.',
              description: 'Past care history lookback explanation',
            })}
          />
          {groups.map(({ programName, providers }) => (
            <Group key={programName ?? 'unknown'} testID={tID('PastCare-group')}>
              <BodyText text={getPastCareProgramLabel(intl, programName)} />
              {providers.map(({ provider, episodes }) => {
                const name =
                  provider.display_name?.trim() ||
                  intl.formatMessage({
                    defaultMessage: 'Provider',
                    description: 'Fallback name for a past care provider',
                  })
                const isSelf = provider.participant_lyra_id === providerId
                const providerLabel = isSelf
                  ? intl.formatMessage(
                      {
                        defaultMessage: '{providerName} (yourself)',
                        description: 'Past care label for the viewing provider',
                      },
                      { providerName: name },
                    )
                  : name
                const disabled = !canMessage(provider)
                const dates = formatPastCareDates(intl, episodes)
                return (
                  <Row
                    key={provider.participant_lyra_id}
                    testID={tID(`PastCare-provider-${provider.participant_lyra_id}`)}
                  >
                    <Avatar
                      details={{ src: provider.profile_image_url ?? '', displayName: name }}
                      size={40}
                      accessibilityLabel={name}
                    />
                    <ProviderInfo testID={tID(`PastCare-providerInfo-${provider.participant_lyra_id}`)}>
                      <BodyText size={BodyTextSize.SMALL} text={providerLabel} />
                      {dates && (
                        <BodyText regular size={BodyTextSize.SMALL} color={colors.textSecondary} text={dates} />
                      )}
                    </ProviderInfo>
                    {canAccessProviderPairs && !isSelf && (
                      <MessageButton
                        testID={tID(`PastCare-message-${provider.participant_lyra_id}`)}
                        disabled={disabled}
                        accessibilityState={{ disabled }}
                        accessibilityLabel={intl.formatMessage(
                          { defaultMessage: 'Message {providerName}', description: 'Message a past care provider' },
                          { providerName: name },
                        )}
                        onPress={() => onMessageProvider(provider.participant_lyra_id)}
                      >
                        <MessageIcon size={24} />
                      </MessageButton>
                    )}
                  </Row>
                )
              })}
            </Group>
          ))}
        </Contents>
      }
    />
  )
}
